import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Paper,
  ScrollArea,
  Skeleton,
  Text,
  Textarea,
  Title,
  useMantineColorScheme,
} from "@mantine/core";
import ApiSession from "../../api/index.js";
import displayError from "../../constants/utils";
import {
  daysAgo,
  statusToOpenClosed,
  getInitials,
  normalizeTicketEnvelope,
  getApiErrorMessage,
  downloadBase64File,
  formatRelativeTime,
} from "../../constants/helper";
import formatToUTCWithGMT from "../../utils/dateHelper";
import { userScopeAtom } from "../../utils/atoms/userScopeAtom.js";
import { useAtom } from "jotai";

const apiSession = new ApiSession();

const TimelineItem = ({ children, isLast, nodeContent, nodeColor, layout }) => (
  <Box
    sx={{
      position: "relative",
      paddingLeft: 52,
      paddingBottom: isLast ? 0 : layout.itemGap,
    }}
  >
    <Box
      sx={{
        position: "absolute",
        left: layout.nodeLeft,
        top: layout.nodeTop,
        width: layout.nodeSize,
        height: layout.nodeSize,
        borderRadius: 999,
        backgroundColor: nodeColor,
        border: `2px solid ${layout.lineColor}`,
        zIndex: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {nodeContent}
    </Box>

    {!isLast && (
      <Box
        sx={{
          position: "absolute",
          left: layout.lineX,
          top: layout.connectorTop,
          bottom: -layout.connectorOverlap,
          width: 2,
          backgroundColor: layout.lineColor,
          zIndex: 1,
        }}
      />
    )}

    {children}
  </Box>
);


const TicketDetails = () => {
  const { ticketId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { colorScheme } = useMantineColorScheme();
  const isLight = colorScheme === "light";
  const [userScope] = useAtom(userScopeAtom);

  const [commentDraft, setCommentDraft] = useState("");
  const [commentError, setCommentError] = useState("");
  const [ticket, setTicket] = useState(() => normalizeTicketEnvelope(state?.ticket));
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [closing, setClosing] = useState(false);

  const handleDownloadFirstComment = () => {
    downloadBase64File(
      ticket?.fileData,
      ticket?.fileContentType,
      ticket?.fileName || `ticket-${ticket?.ticketId || "attachment"}`,
    );
  };

  useEffect(() => {
    setComments(Array.isArray(ticket?.comments) ? ticket.comments : []);
  }, [ticket?.comments]);

  const status = statusToOpenClosed(ticket?.status);
  const isTicketClosed = String(ticket?.status ?? "").toLowerCase() === "closed";
  const canClose = status?.label === "In progress";

  const pageBg = isLight ? "#fff" : "var(--se-background-alternative)";
  const subtleBorder = isLight ? "1px solid #e9ecef" : "1px solid #2b2b2b";
  const timelineColor = isLight ? "#dee2e6" : "#3a3a3a";
  const headerBg = isLight ? "#fff" : "var(--se-background-alternative)";

  const timelineLayout = {
    nodeTop: 18,
    nodeLeft: 9,
    nodeSize: 28,
    lineX: 9 + 28 / 2,
    connectorTop: 18 + 28,
    itemGap: 14,
    connectorOverlap: 18 + 28 / 2,
    lineColor: timelineColor,
  };

  const MAX_COMMENT_LENGTH = 1000;

  const handleCloseTicket = async () => {
    if (!ticket?.ticketId || !canClose) return;
    setClosing(true);
    try {
      await apiSession.closeTicket(ticket.ticketId);
      try {
        const refreshed = await apiSession.getTicketId(ticket.ticketId);
        const data = refreshed?.data ?? refreshed;
        setTicket(normalizeTicketEnvelope(data));
      } catch (refreshErr) {
        // eslint-disable-next-line no-console
        console.error("Failed to refresh closed ticket", refreshErr);
        displayError(
          "error",
          getApiErrorMessage(
            refreshErr,
            t("ticketDetails.errors.refreshTicket"),
          ),
        );
      }

      displayError(
        "success",
        t("ticketDetails.close.success"),
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to close ticket", err);
      const message = getApiErrorMessage(
        err,
        t("ticketDetails.errors.closeTicket"),
      );
      displayError("error", message);
    } finally {
      setClosing(false);
    }
  };

  useEffect(() => {
    const refreshed = apiSession
      .getTicketId(ticketId)
      .then((refreshed) => {
        const data = refreshed?.data ?? refreshed;
        setTicket(normalizeTicketEnvelope(data));
      })
      .catch((refreshErr) => {
        // eslint-disable-next-line no-console
        console.error("Failed to refresh ticket", refreshErr);
        displayError(
          "error",
          getApiErrorMessage(
            refreshErr,
            t("ticketDetails.errors.refreshTicket"),
          ),
        );
      });
  }, []);

  const handleSubmitComment = async () => {
    const body = commentDraft.trim();
    if (!body || !ticket?.ticketId) return;
    if (body?.length > MAX_COMMENT_LENGTH) {
      const tooLongMessage = t("ticketDetails.errors.commentTooLong");
      setCommentError(tooLongMessage);
      displayError("error", tooLongMessage);
      return;
    }
    setSubmitting(true);
    try {
      await apiSession.saveComments(ticket?.ticketId, { content: body });
      const nextComment = {
        commentId: `local-${Date.now()}`,
        authorId: ticket?.userId || ticket?.createdBy,
        authorRole: "USER",
        content: body,
        createdAt: new Date().toISOString(),
      };
      setTicket((prev) => ({
        ...prev,
        comments: [...(Array.isArray(prev?.comments) ? prev.comments : []), nextComment],
      }));
      setCommentDraft("");
      setCommentError("");

      try {
        const refreshed = await apiSession.getTicketId(ticket.ticketId);
        const data = refreshed?.data ?? refreshed;
        setTicket(normalizeTicketEnvelope(data));
      } catch (refreshErr) {
        // eslint-disable-next-line no-console
        console.error("Failed to refresh ticket", refreshErr);
        displayError(
          "error",
          getApiErrorMessage(
            refreshErr,
            t("ticketDetails.errors.refreshTicket"),
          ),
        );
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to save comment", err);
      const message = getApiErrorMessage(
        err,
        t("ticketDetails.errors.saveComment"),
      );
      displayError("error", message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <ScrollArea style={{ height: "calc(115%)" }}>
        <Box sx={{ padding: 16 }}>
          <Box sx={{ maxWidth: 980, margin: "0 auto" }}>
            <Paper radius="md" sx={{ padding: 16, marginBottom: 16 }}>
              <Skeleton height={24} width="60%" mb={10} />
              <Skeleton height={12} width="30%" mb={4} />
              <Skeleton height={12} width="40%" />
            </Paper>

            {[1, 2, 3, 4].map((key) => (
              <Paper key={key} radius="md" sx={{ padding: 16, marginBottom: 14 }}>
                <Group spacing={12} align="flex-start">
                  <Skeleton height={32} circle />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton height={14} width="22%" mb={6} />
                    <Skeleton height={12} width="35%" mb={12} />
                    <Skeleton height={10} width="100%" mb={6} />
                    <Skeleton height={10} width="96%" mb={6} />
                    <Skeleton height={10} width="88%" />
                  </Box>
                </Group>
              </Paper>
            ))}
          </Box>
        </Box>
      </ScrollArea>
    );
  }

  if (!ticket) {
    return (
      <ScrollArea style={{ height: "calc(115%)" }}>
        <Box sx={{ padding: 24 }}>
          <Group position="apart" align="center" sx={{ marginBottom: 8 }}>
            <Text weight={700}>{t("ticketDetails.notFound")}</Text>
            <Button aria-label="ticket-back-not-found" variant="subtle" onClick={() => navigate("/tickets")}>
              {t("common.back")}
            </Button>
          </Group>
        </Box>
      </ScrollArea>
    );
  }

  return (
    <>
      <se-snackbar
        id="snackbar2"
        icon="information_stroke"
        canClose={true}
      ></se-snackbar>
      <ScrollArea style={{ height: "calc(115%)" }}>
        <Box sx={{ paddingTop: 16, paddingBottom: 32 }}>
          <Box
            sx={{
              maxWidth: 980,
              marginLeft: "auto",
              marginRight: "auto",
              paddingLeft: 16,
              paddingRight: 16,
            }}
          >
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                backgroundColor: headerBg,
                paddingTop: 8,
                paddingBottom: 12,
                borderBottom: subtleBorder,
                marginBottom: 16,
              }}
            >
              <Group position="apart" align="flex-start">
                <Box sx={{ minWidth: 0 }}>
                  <Title
                    order={3}
                    sx={{ fontFamily: "Nunito", wordBreak: "break-word" }}
                  >
                    {ticket?.ticketType}: {ticket?.ticketTitle}
                  </Title>
                  <Group spacing="xs" sx={{ marginTop: 8 }}>
                    <Badge color={status?.color} variant="filled" radius="sm">
                      {status?.label}
                    </Badge>
                    <Text size="sm" color={isLight ? "dimmed" : "gray.4"}>
                      #{ticket?.ticketId}
                    </Text>
                  </Group>
                </Box>
                <Button aria-label="ticket-back-header" variant="subtle" onClick={() => navigate("/tickets")}>
                  {t("common.back")}
                </Button>
              </Group>
            </Box>

            <Box sx={{ position: "relative" }}>
              <TimelineItem
                layout={timelineLayout}
                isLast={comments.length === 0}
                nodeContent={
                  <Text
                    size="xs"
                    weight={700}
                    sx={{ fontFamily: "Nunito", lineHeight: 1 }}
                  >
                    {getInitials(ticket.createdBy)}
                  </Text>
                }
                nodeColor={isLight ? "#f8f9fa" : "#2b2b2b"}
              >
                <Paper
                  radius="md"
                  sx={{
                    backgroundColor: pageBg,
                    border: subtleBorder,
                    padding: 16,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Group spacing={6}>
                      <Text weight={700} sx={{ fontFamily: "Nunito" }}>
                        {ticket.createdBy}
                      </Text>
                      <Text size="sm" color={isLight ? "dimmed" : "gray.4"}>
                        opened {daysAgo(ticket.createdOn)}
                      </Text>
                    </Group>
                    <Divider sx={{ marginTop: 12, marginBottom: 12 }} />
                    <Text sx={{ fontFamily: "Nunito", whiteSpace: "pre-wrap" }}>
                      {ticket.ticketDesc}
                    </Text>
                    {ticket?.fileData && (
                      <Text sx={{ fontFamily: "Nunito", marginTop: 8 }}>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleDownloadFirstComment();
                          }}
                          style={{
                            textDecoration: "underline",
                            textUnderlineOffset: "3px",
                          }}
                        >
                          {t("ticket.download.attachement")}
                        </a>
                      </Text>
                    )}
                  </Box>
                </Paper>
              </TimelineItem>

              {comments.map((c, idx) => (
                <TimelineItem
                  layout={timelineLayout}
                  key={c.commentId}
                  isLast={idx === comments.length - 1}
                  nodeContent={
                    <Text
                      size="xs"
                      weight={700}
                      sx={{ fontFamily: "Nunito", lineHeight: 1 }}
                    >
                      {getInitials(c.authorId)}
                    </Text>
                  }
                  nodeColor={isLight ? "#fff" : "#2b2b2b"}
                >
                  <Paper
                    radius="md"
                    sx={{
                      backgroundColor: pageBg,
                      border: subtleBorder,
                      padding: 16,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Group spacing={6}>
                        <Text weight={700} sx={{ fontFamily: "Nunito" }}>
                          {c.authorId}
                        </Text>
                        <Badge
                          variant="light"
                          color={c.authorRole === "ASSIGNEE" ? "blue" : "gray"}
                        >
                          {c.authorRole}
                        </Badge>
                        <Text size="sm" color={isLight ? "dimmed" : "gray.4"}>
                          commented{" "}
                          {formatRelativeTime(formatToUTCWithGMT(c?.updatedAt))}
                        </Text>
                      </Group>
                      <Divider sx={{ marginTop: 12, marginBottom: 12 }} />
                      <Text
                        sx={{ fontFamily: "Nunito", whiteSpace: "pre-wrap" }}
                      >
                        {c.content}
                      </Text>
                    </Box>
                  </Paper>
                </TimelineItem>
              ))}

              {!isTicketClosed && (
                <TimelineItem
                  layout={timelineLayout}
                  isLast
                  nodeContent={
                    <Text
                      size="xs"
                      weight={700}
                      sx={{ fontFamily: "Nunito", lineHeight: 1 }}
                    >
                      {getInitials(userScope?.email)}
                    </Text>
                  }
                  nodeColor={isLight ? "#fff" : "#2b2b2b"}
                >
                <Paper
                  radius="md"
                  sx={{
                    backgroundColor: pageBg,
                    border: subtleBorder,
                    padding: 16,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Textarea
                      placeholder={t("ticketDetails.comment.placeholder")}
                      minRows={4}
                      value={commentDraft}
                      onChange={(event) => {
                        const nextValue = event.currentTarget.value;
                        const tooLongMessage = t("ticketDetails.errors.commentTooLong");
                        if (nextValue.length > MAX_COMMENT_LENGTH) {
                          setCommentError(tooLongMessage);
                          setCommentDraft(
                            nextValue.slice(0, MAX_COMMENT_LENGTH),
                          );
                          return;
                        }

                        setCommentError("");
                        setCommentDraft(nextValue);
                      }}
                      error={commentError}
                      description={`${commentDraft.length}/${MAX_COMMENT_LENGTH}`}
                      autosize
                    />
                    <Group position="right" spacing="sm" sx={{ marginTop: 12 }}>
                      <Button
                        aria-label="ticket-close-ticket"
                        variant="outline"
                        color="red"
                        onClick={handleCloseTicket}
                        disabled={!canClose || closing}
                        loading={closing}
                      >
                        {t("ticketDetails.close.label")}
                      </Button>
                      <Button
                        aria-label="ticket-cancel-comment"
                        variant="default"
                        onClick={() => setCommentDraft("")}
                        disabled={!commentDraft}
                      >
                        {t("common.cancel")}
                      </Button>
                      <Button
                        aria-label="ticket-submit-comment"
                        color="seGreen.0"
                        onClick={handleSubmitComment}
                        disabled={!commentDraft || submitting || commentError}
                        loading={submitting}
                      >
                        {t("common.submit")}
                      </Button>
                    </Group>
                  </Box>
                </Paper>
                </TimelineItem>
              )}
            </Box>
          </Box>
        </Box>
      </ScrollArea>
    </>
  );
};

export default TicketDetails;
